/**
 * The one question a paragraph answers, and the gaps a composed one declares.
 *
 * Replaces three separate controls — the provenance action, the section type
 * and the organisation-block role — of which the third silently overrode the
 * other two. A paragraph marked as written per case by a model, carrying a
 * role, rendered as the factory's standing text and no model ever ran; the
 * screen showed the tags, so it said one thing while the document did another.
 *
 * The six answers come off the API (`paragraph_kinds` on the template detail),
 * never from a list held here. A screen carrying its own copy of six can offer
 * a seventh, and the two wordings disagree the first time one is edited — the
 * same argument as `readPeriod()` and `explain()`.
 */

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { api } from '../api/client'
import type { components } from '../api/schema'

type ParagraphKind = components['schemas']['ParagraphKind']
type ParagraphStyle = components['schemas']['ParagraphStyle']
type TemplateBlockView = components['schemas']['TemplateBlockView']
type GapDeclaration = components['schemas']['GapDeclaration']

/** Every `{{marker}}` in the words, in the order they appear, deduplicated.
 *
 * The officer's sentence is the authority on where the holes are: a gap is
 * offered for a marker that is in the text, and never for one that is not. The
 * server refuses the other case anyway — this is what stops the officer being
 * shown a row they cannot save. */
export function markersIn(text: string): string[] {
  const found = [...text.matchAll(/\{\{([^{}]+)\}\}/g)]
    .map((m) => (m[1] ?? '').trim().split('.')[0] ?? '')
    .filter((key) => key.length > 0)
  return [...new Set(found)]
}

export function ParagraphKindPicker({
  documentType,
  block,
  kinds,
  editable,
  onChanged,
  language,
}: {
  documentType: string
  block: TemplateBlockView
  kinds: ParagraphKind[]
  editable: boolean
  onChanged: () => void
  language: 'hi' | 'en'
}) {
  const setKind = useMutation({
    mutationFn: (kind: string) =>
      api.setBlockKind(documentType, {
        preflight: false,
        order: block.order,
        kind,
        language,
      } as never),
    onSuccess: onChanged,
  })

  const current = kinds.find((k) => k.value === block.kind)

  // Statutory text has no editing affordance at all — not a disabled picker,
  // which an officer reads as a bug and reports. The answer is stated, with
  // where the words come from, and there is nothing to click.
  if (block.kind === 'statutory') {
    return (
      <div className="kind locked" data-testid={`kind-${block.order}`}>
        <div className="strong">It is the law</div>
        <div className="small muted">
          Reproduced from the rule corpus, with its citation. It cannot be edited, removed or
          rewritten — here or anywhere else, by anyone. Its position on the page is yours to
          change.
        </div>
      </div>
    )
  }

  return (
    <div className="kind" data-testid={`kind-${block.order}`}>
      <div className="small muted">What is this paragraph?</div>
      {block.kind === null ? (
        <div className="small warn" data-testid={`kind-unanswered-${block.order}`}>
          Not answered yet. This paragraph came from the skeleton with nothing recorded about
          what it is, so nothing has been guessed onto it.
        </div>
      ) : null}
      <select
        aria-label={`What paragraph ${block.order} is`}
        value={block.kind ?? ''}
        disabled={!editable || setKind.isPending}
        onChange={(e) => setKind.mutate(e.target.value)}
        data-testid={`kind-select-${block.order}`}
      >
        {block.kind === null ? <option value="">— choose —</option> : null}
        {kinds
          .filter((k) => k.choosable)
          .map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
      </select>
      {current ? (
        <div className="small muted">
          {current.meaning}{' '}
          <span className="mono">Model: {current.model_touches.toLowerCase()}</span>
        </div>
      ) : null}
      {setKind.error ? (
        <div className="small warn" role="alert">
          {String((setKind.error as Error).message)}
        </div>
      ) : null}
    </div>
  )
}

/**
 * One paragraph's shape — one dropdown, not four controls.
 *
 * The real documents have about six shapes and no more. Offering alignment,
 * indent, weight and spacing separately produces paragraph 3 looking unlike
 * paragraph 2 for a reason nobody can state, and a noting whose numbered
 * paragraphs do not line up looks careless in a hearing.
 *
 * The example is what an administrator picks by — a line they have written a
 * hundred times, not a description of an indent. What each shape *means* is
 * set once per factory, in Settings, and the link says so: an officer who
 * changes it here expects the millimetres to be here too.
 *
 * Statutory text gets this control like anything else. The lock is on the
 * words, not on where they sit.
 */
export function ParagraphStylePicker({
  documentType,
  block,
  styles,
  editable,
  onChanged,
  language,
}: {
  documentType: string
  block: TemplateBlockView
  styles: ParagraphStyle[]
  editable: boolean
  onChanged: () => void
  language: 'hi' | 'en'
}) {
  const set = useMutation({
    mutationFn: (style: string | null) =>
      api.setBlockStyle(documentType, { preflight: false, order: block.order, style, language }),
    onSuccess: onChanged,
  })

  return (
    <div className="kind" data-testid={`style-${block.order}`}>
      <label className="small muted">
        What shape is it?
        <select
          aria-label={`The shape of paragraph ${block.order}`}
          value={block.style ?? ''}
          disabled={!editable || set.isPending}
          onChange={(e) => set.mutate(e.target.value === '' ? null : e.target.value)}
          data-testid={`style-select-${block.order}`}
        >
          <option value="">— not set —</option>
          {styles.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label} — {s.example}
            </option>
          ))}
        </select>
      </label>
      <div className="small faint">
        {block.style
          ? 'What this shape means — the indent, the space after — is set once for this factory in Settings.'
          : 'Not set. This paragraph is laid out from the spacing its words already carry, which is a guess and changes if the line is retyped.'}
      </div>
      {set.error ? (
        <div className="small warn" role="alert">
          {String((set.error as Error).message)}
        </div>
      ) : null}
    </div>
  )
}

/**
 * The gaps in a composed paragraph, and what belongs in each.
 *
 * One instruction per gap, and the whole set is written at once: an officer
 * writing these is deciding how the sentence divides, not editing rows, and a
 * partial write would leave the gaps and the words disagreeing.
 *
 * The panel says out loud that the sentence is never sent. That is not
 * reassurance — it is the fact that tells the officer why the instruction has
 * to stand on its own.
 */
export function GapsPanel({
  documentType,
  block,
  editable,
  onChanged,
  language,
}: {
  documentType: string
  block: TemplateBlockView
  editable: boolean
  onChanged: () => void
  language: 'hi' | 'en'
}) {
  const markers = markersIn(block.text_hi)
  const declared = new Map((block.gaps ?? []).map((g) => [g.key, g.instruction]))
  const [draft, setDraft] = useState<Record<string, string>>({})

  const save = useMutation({
    mutationFn: (gaps: GapDeclaration[]) =>
      api.setBlockGaps(documentType, {
        preflight: false,
        order: block.order,
        gaps,
        language,
      } as never),
    onSuccess: () => {
      setDraft({})
      onChanged()
    },
  })

  if (block.kind !== 'composed') return null

  const valueFor = (key: string) => draft[key] ?? declared.get(key) ?? ''
  const dirty = Object.keys(draft).some((k) => draft[k] !== (declared.get(k) ?? ''))

  return (
    <div className="gaps" data-testid={`gaps-${block.order}`}>
      <div className="small muted">
        The model is never shown this sentence, so it cannot change it. It is handed one gap and
        told what belongs in it.
      </div>

      {markers.length === 0 ? (
        <div className="small warn" data-testid={`gaps-none-${block.order}`}>
          This paragraph is composed per case but marks no gaps, so the model would have nothing
          to write. Mark the words that change with <span className="mono">{'{{a name}}'}</span>
          .
        </div>
      ) : null}

      {markers.map((key) => (
        <label key={key} className="gap">
          <span className="mono">{`{{${key}}}`}</span>
          <input
            type="text"
            value={valueFor(key)}
            disabled={!editable || save.isPending}
            placeholder="What belongs in this gap, in your own words"
            aria-label={`What belongs in ${key}`}
            onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
            data-testid={`gap-${block.order}-${key}`}
          />
        </label>
      ))}

      {markers.length > 0 && editable ? (
        <button
          type="button"
          className="quiet"
          disabled={!dirty || save.isPending}
          onClick={() =>
            save.mutate(markers.map((key) => ({ key, instruction: valueFor(key) })))
          }
          data-testid={`gaps-save-${block.order}`}
        >
          Save what the gaps want
        </button>
      ) : null}

      {save.error ? (
        <div className="small warn" role="alert">
          {String((save.error as Error).message)}
        </div>
      ) : null}
    </div>
  )
}
